import {
	extractUpdatedFields,
	isEmptyObject,
	pickFields,
	sanitizeData,
} from "nhb-toolbox";
import type { UseFormReset } from "react-hook-form";
import Spinner from "src/components/Spinner/Spinner";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import { toast } from "src/Toaster/Toaster";
import type { IAccountDetails, ICreateAccount } from "src/Types/account.types";
import AccountForm from "./AccountForm";

type IProps = {
	id: number;
	handleClose: () => void;
};

const UpdateAccount = ({ id, handleClose }: IProps) => {
	const { data: account, isLoading } = useApiQuery<IAccountDetails>({
		endPoint: `/api/fair/admin/account/${id}`,
		queryKeys: ["Account", id],
	});

	const { mutate: updateAccount, isPending } = useApiMutation<
		Partial<ICreateAccount>,
		void
	>({
		method: `PATCH`,
		endPoint: `/api/fair/admin/account/${id}`,
		invalidateQueries: ["Accounts", "Account", ["Account", id]],
	});

	const handleUpdateAccount = (
		formData: ICreateAccount,
		reset: UseFormReset<ICreateAccount>
	) => {
		const previousData: ICreateAccount = pickFields(account, [
			"ac_name",
			"ac_type",
			"ac_details",
			"ac_number",
			"ac_bank_name",
			"ac_bank_branch",
			"opening_balance",
		]);

		const sanitizedData = sanitizeData(formData, {
			keysToIgnore: ["opening_balance"],
		});

		const updatedData = extractUpdatedFields(previousData, sanitizedData);

		if (isEmptyObject(updatedData)) {
			return toast.fire({
				icon: "warning",
				title: "Nothing to update!",
				position: "top",
			});
		}

		updateAccount(updatedData);
		handleClose();
		reset();
	};

	if (isLoading) {
		return <Spinner />;
	}

	return (
		<AccountForm
			previousData={account}
			isLoading={isPending}
			onSubmit={handleUpdateAccount}
		/>
	);
};

export default UpdateAccount;
